import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { SessionsService } from '../sessions.service';
import { ISession } from '../../../models/session';
import { TranslateService } from '@ngx-translate/core';
import { environment as config } from '../../../../environments/environment';
import { merge } from 'ramda/es';
import * as languageList from 'language-list';
import * as moment from 'moment';
import { first, map, tap, filter } from 'rxjs/operators';
import { app_themes } from '../../../theme/themes.const';
import { ThemeService } from '../../../theme/theme.service';
import * as store from 'store';
import { ThemeDirective } from '../../../theme/theme.directive';
import { eva_constants } from '../../../eva_constants';
import { CustomizationService } from '../../customization/customization.service';
import { union } from 'ramda/es';
import { ITag } from '../../../models/tag';
import { ICategory } from '../../../models/category';
import { FormControl, FormBuilder, Validators } from '@angular/forms';
import { UsersService } from '../users/users.service';
import { equals, isEmpty, isNil } from 'ramda';
import { EmailService } from '../../email/email.service';
import { PluginsService } from '../../plugins/plugins.service';
import { getApiErrorMessage } from '../../../providers/api-error';

const toCode = lang => languageList().getLanguageCode(lang);

@Component({
  templateUrl: './session-edit.component.html',
  styleUrls: ['./session-edit.component.scss', '../../../../assets/styles/imports/_ngxchips.scss']
})
export class SessionsEditComponent implements OnInit {
  public session: ISession;
  public sessionForm: any;
  public formatedDate;
  public isPast;
  public shortBaseUrl = config.livee.short;
  public iframe;
  // Subscribe
  private lang$;
  private accountType$;
  public theme: String = eva_constants.EVA_THEME;
  public allCategories: Array<ICategory>;
  public showGenericLoginModal: boolean = false;
  public genericLoginForm: any;
  public genericData: any;
  public sessionConfig = {
    allowChangeSessionType: false
  };

  constructor(
    private route: ActivatedRoute,
    private sessionsService: SessionsService,
    private translateService: TranslateService,
    private customizationService: CustomizationService,
    private usersService: UsersService,
    private router: Router,
    private emailService: EmailService,
    private pluginsService: PluginsService,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder
  ) {
    this.sessionForm = sessionsService.sessionForm;
    this.genericLoginForm = this.fb.group({
      login: ['', Validators.required],
      password: ['', Validators.required]
    });
  }

  isMyLiveTV() {
    return this.theme === eva_constants.MYLIVETV_THEME;
  }

  ngOnDestroy() {
    if (this.lang$) {
      this.lang$.unsubscribe();
    }
    if (this.accountType$) {
      this.accountType$.unsubscribe();
    }
  }

  ngOnInit() {
    if (this.route && this.route.data) {
      this.route.data.subscribe(data => {
        this.bindTheme(data);
      });
    }

    this.sessionsService.getAllCategories().subscribe(categories => {
      this.allCategories = categories;
    });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        tap(() => {
          if (!this.route.snapshot.firstChild) return;
          const { data } = this.route.snapshot.firstChild;
          this.bindTheme(data);
        })
      )
      .subscribe(null);

    const onError = message => err => {
      console.error(err);
      toastr.error(this.translateService.instant(message));
    };

    const onSession = (session: ISession) => {
      this.iframe = this.makeIframeUrl(session);
      this.session = session;
      const date = moment(session.date);
      date.locale(langCode);
      this.formatedDate = date.format('LL');
      this.cdr.detectChanges();
      const diff = moment(session.date).diff(moment(), 'days');
      this.isPast = diff < 0 && session.status === 'STOPPED';
      this.lang$ = this.translateService.onLangChange.subscribe(event => {
        const newCode = toCode(event.lang);
        date.locale(newCode);
        this.formatedDate = date.format('LL');
        this.cdr.detectChanges();
      });
      this.sessionsService.getAllSessionCategories(this.session).subscribe(categories => {
        this.sessionForm.patchValue({ categories });
      });

      // Add form controls for accountType and sessionType if they don't exist
      if (!this.sessionForm.get('accountType')) {
        this.sessionForm.addControl('accountType', new FormControl(''));
      }
      if (!this.sessionForm.get('sessionType')) {
        this.sessionForm.addControl('sessionType', new FormControl(''));
      }

      // Patch values from session.types
      this.sessionForm.patchValue(
        {
          accountType: session.types.accountType,
          sessionType: session.types.sessionType
        },
        { emitEvent: false }
      );
      this.sessionForm.get('sessionType').enable();

      if (this.accountType$) {
        this.accountType$.unsubscribe();
      }
      this.accountType$ = this.sessionForm.get('accountType').valueChanges.subscribe(value => {
        if (value === 'generic_login') {
          this.genericData = null;
          this.showGenericLoginModal = true;
          this.sessionForm.get('sessionType').disable();
          this.genericLoginForm.reset();
        }
      });

      if (session.types.accountType === 'generic_login') {
        this.usersService.getGenericLogin(session.id).subscribe(data => {
          this.genericData = data;
        });
      }
    };

    const locale = this.translateService.currentLang || config.translations.default;
    const langCode = toCode(locale); // we need to translate the date, and moment needs a languageCode

    const parentData$ = this.route.parent.data;

    parentData$
      .pipe(
        first(),
        map(route => route.data),
        tap(onSession)
      )
      .subscribe(null, onError('action.failed'));

      this.customizationService.getSessionConfig().subscribe(config => {
        this.sessionConfig = merge(this.sessionConfig, config);
      });
  }

  bindTheme(data) {
    if (!data) return;
    if (!data.theme) data.theme = store.get(ThemeDirective.THEME);

    if (data.theme) {
      this.theme = data.theme;
      if (data.theme === eva_constants.MYLIVETV_THEME) {
        this.shortBaseUrl = this.sessionsService.fixLinkForMyLiveTV(config.livee.short);
      }
    }
  }

  makeIframeUrl(session) {
    return `<iframe src="${session.url}/l/Webevent-00-AllModules-Iframe?forceiframereload=true&forceajaxrequest=true" width="100%" height="100%" frameborder="0" allowfullscreen scrolling="yes"></iframe>`;
  }

  doEdit() {
    // WARNING keep in sync with step3.component.ts addSession()
    const postCreation = [
      {
        func: this.sessionsService.close,
        bind: this.sessionsService,
        data: { sessionType: 'vod' },
        use: (session: ISession) => {
          return session.types.sessionType === 'vod';
        }
      },
      // {
      //   func: this.usersService.updateRegistration,
      //   bind: this.usersService,
      //   data: { data: [] },
      //   use: (session: ISession) => true
      // },
      {
        func: this.usersService.updateGenericLogin,
        bind: this.usersService,
        data: this.genericData,
        use: (session: ISession) => true
      },
      {
        func: this.emailService.applyEmailTemplate,
        bind: this.emailService,
        data: { sessionType: 'interactive' },
        use: (session: ISession) => {
          return session.types.sessionType === 'interactive';
        }
      },
      {
        func: this.pluginsService.toggleOnly,
        bind: this.pluginsService,
        data: { pluginName: 'video_replay_publish' },
        use: (session: ISession) => {
          return session.types.sessionType === 'vod';
        }
      }
    ];

    const formValue = this.sessionForm.value;

    // Merge types object with accountType and sessionType from form
    const updatedTypes = merge(this.session.types || {}, {
      accountType: formValue.accountType,
      privacy: formValue.accountType === 'free' || formValue.accountType === 'registration' ? 'public' : 'private',
      sessionType: formValue.sessionType
    });

    // make sure to detect if session types have changed and generic login/password was set
    const sessionTypeUpdated = !equals(this.session.types, updatedTypes) && (
      formValue.accountType === "generic_login" ? this.genericData !== null : true
    );

    const payload = merge(this.session, merge(formValue, { ...(sessionTypeUpdated && {types: updatedTypes}) }));

    this.sessionsService
      .update(this.session.id, payload)
        .toPromise()
        .then((updatedSession: ISession) => {
          this.session = updatedSession;
          this.sessionsService.setComplete(this.session, 'general');
          return updatedSession;
        })
        .then((updatedSession: ISession) => {
          if (sessionTypeUpdated) {
            return Promise.all(
              postCreation
                // Filter unnecessary post-creation processes
                .filter(f => !isEmpty(f.data) && !isNil(f.data) && f.use(updatedSession))
                .map(f => {
                  const func = f.func.bind(f.bind);
                  return func(updatedSession, f.data, false).toPromise();
                })
            ).then(() => {});            
          } else{
            return Promise.resolve();
          }
        })
        .then(() => {
          const categoryIds = this.sessionForm.controls['categories'].value.map(c => c.id);
          this.sessionsService.updateSessionCategories(this.session, categoryIds).subscribe(null);
        })
        .catch(err => {
          // generic errors are already notified by BasicService.handleError
          const apiMessage = getApiErrorMessage(err);
          if (apiMessage) {
            toastr.error(apiMessage, this.translateService.instant('action.failed'));
          }
        });
  }

  closeGenericLoginModal() {
    this.showGenericLoginModal = false;
  }

  saveGenericLogin() {
    this.genericData = {
      ...this.genericLoginForm.value,
      send_to_all_users: false
    };
    this.showGenericLoginModal = false;
    this.sessionForm.get('sessionType').enable();
  }

  canSave() {
    if (this.sessionForm.value.accountType === 'generic_login') {
      return this.genericData !== null;
    }
    return true;
  }

  onCopySuccess(item: 'phone' | 'link') {
    const key = {
      phone: 'sessions.edit.phone_number.copied_success',
      link: 'sessions.edit.link.copied_success'
    }[item];
    toastr.success(this.translateService.instant(key));
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/sessions/edit/session-edit.component.ts